import React, { useState, useEffect, useCallback } from 'react';
import { WorkProfileGroup, WORK_PROFILE_GROUP_LABELS } from '@gramio/shared';
import * as systemSettingsService from '../services/systemSettingsService';

const KEY_STANDARD = 'payout_standard_commission_percent';
const KEY_REDUCED = 'payout_reduced_commission_percent';
const KEY_FIXED = 'payout_fixed_fee_by_work_profile';

const WORK_GROUPS = Object.values(WorkProfileGroup);

function numOrDefault(v: unknown, fallback: number): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}

export const PayoutCommissionPolicyPanel: React.FC = () => {
  const [standardPct, setStandardPct] = useState('15');
  const [reducedPct, setReducedPct] = useState('10');
  const [fixedFees, setFixedFees] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [std, red, fix] = await Promise.all([
        systemSettingsService.getPlatformConfig(KEY_STANDARD).catch(() => null),
        systemSettingsService.getPlatformConfig(KEY_REDUCED).catch(() => null),
        systemSettingsService.getPlatformConfig(KEY_FIXED).catch(() => null),
      ]);
      setStandardPct(String(numOrDefault(std?.configValue, 15)));
      setReducedPct(String(numOrDefault(red?.configValue, 10)));
      const rawFixed =
        fix?.configValue && typeof fix.configValue === 'object' && !Array.isArray(fix.configValue)
          ? (fix.configValue as Record<string, unknown>)
          : {};
      const next: Record<string, string> = {};
      for (const g of WORK_GROUPS) {
        const n = numOrDefault(rawFixed[g], NaN);
        next[g] = Number.isFinite(n) ? String(n) : '';
      }
      setFixedFees(next);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load payout fee policy');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const upsertNumberConfig = async (
    key: string,
    value: number,
    description: string
  ): Promise<void> => {
    try {
      await systemSettingsService.updatePlatformConfig(key, { configValue: value });
    } catch {
      await systemSettingsService.createPlatformConfig({
        configKey: key,
        configValue: value,
        description,
        category: 'commission',
      });
    }
  };

  const save = async () => {
    const s = Number(standardPct);
    const r = Number(reducedPct);
    if (!Number.isFinite(s) || s < 0 || s > 90) {
      setMessage(null);
      setError('Standard % must be between 0 and 90.');
      return;
    }
    if (!Number.isFinite(r) || r < 0 || r > 90) {
      setMessage(null);
      setError('Reduced % must be between 0 and 90.');
      return;
    }
    const fixedObj: Record<string, number> = {};
    for (const g of WORK_GROUPS) {
      const t = (fixedFees[g] ?? '').trim();
      if (t === '') continue;
      const n = Number(t);
      if (!Number.isFinite(n) || n < 0) {
        setMessage(null);
        setError(`Invalid fixed fee for ${WORK_PROFILE_GROUP_LABELS[g]}`);
        return;
      }
      fixedObj[g] = n;
    }

    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await upsertNumberConfig(
        KEY_STANDARD,
        s,
        'Standard platform commission % on provider payouts (default tier).'
      );
      await upsertNumberConfig(
        KEY_REDUCED,
        r,
        'Reduced platform commission % when provider chooses the lower-rate option.'
      );
      try {
        await systemSettingsService.updatePlatformConfig(KEY_FIXED, { configValue: fixedObj });
      } catch {
        await systemSettingsService.createPlatformConfig({
          configKey: KEY_FIXED,
          configValue: fixedObj,
          description:
            'Fixed INR platform fee per paid booking (fixed_per_booking model), by work profile group id.',
          category: 'commission',
        });
      }
      setMessage('Payout commission policy saved.');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="payout-commission-policy-panel payout-policy-loading">
        Loading payout commission policy…
      </div>
    );
  }

  return (
    <div className="payout-commission-policy-panel">
      <h3 className="payout-policy-title">Payout commission policy</h3>
      <p className="payout-policy-description">
        Controls provider-facing subscription options: <strong>standard</strong> and <strong>reduced</strong> percentages,
        and <strong>fixed fees per job category</strong> (work profile group). Monthly unlimited stays 0% per booking
        while the plan is active; the subscription itself is handled outside payouts.
      </p>
      <div className="payout-policy-fields">
        <label className="payout-policy-row">
          <span className="payout-policy-label">Standard commission (% of each payout)</span>
          <input
            type="number"
            min={0}
            max={90}
            step={0.1}
            value={standardPct}
            onChange={(e) => setStandardPct(e.target.value)}
            disabled={saving}
            className="payout-policy-input payout-policy-input-pct"
          />
        </label>
        <label className="payout-policy-row">
          <span className="payout-policy-label">Reduced-rate option (% of each payout)</span>
          <input
            type="number"
            min={0}
            max={90}
            step={0.1}
            value={reducedPct}
            onChange={(e) => setReducedPct(e.target.value)}
            disabled={saving}
            className="payout-policy-input payout-policy-input-pct"
          />
        </label>
      </div>
      <h4 className="payout-policy-subtitle">Fixed fee per booking (₹) by service category</h4>
      <p className="payout-policy-description">
        Used when a provider selects <strong>fixed per booking</strong>. Leave blank to fall back to standard % for that
        category. Capped at the customer payment amount per booking.
      </p>
      <div className="payout-policy-fees">
        {WORK_GROUPS.map((g) => (
          <label key={g} className="payout-policy-row">
            <span className="payout-policy-label">{WORK_PROFILE_GROUP_LABELS[g]}</span>
            <input
              type="number"
              min={0}
              step={1}
              placeholder="—"
              value={fixedFees[g] ?? ''}
              onChange={(e) => setFixedFees((prev) => ({ ...prev, [g]: e.target.value }))}
              disabled={saving}
              className="payout-policy-input payout-policy-input-fee"
            />
          </label>
        ))}
      </div>
      <div className="payout-policy-actions">
        <button type="button" className="payout-policy-btn payout-policy-btn-primary" disabled={saving} onClick={() => void save()}>
          {saving ? 'Saving…' : 'Save payout policy'}
        </button>
        <button type="button" className="payout-policy-btn payout-policy-btn-secondary" disabled={saving} onClick={() => void load()}>
          Reload
        </button>
      </div>
      {message ? <p className="payout-policy-message success">{message}</p> : null}
      {error ? <p className="payout-policy-message error">{error}</p> : null}
    </div>
  );
};
